import { useAuth } from '@/store/auth.store'
import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import {
  calendarDateFromPicker,
  calendarDateFromStorage,
  calendarDateToPicker,
  calendarDateToStorage,
} from '@scrambl/shared'

/**
 * App-wide date format convention:
 * - `display` for all read-only text and picker trigger labels
 * - `displayNoYear` for hideYear / same-year contexts
 * - `input` ONLY for typed text inputs (masks) — never for read-only display
 */
export const DATE_FORMATS = {
  display: 'MMM d, yyyy',
  displayNoYear: 'MMM d',
  input: 'MM/dd/yyyy',
} as const

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
// Two instants mean "a calendar date, not a time": UTC midnight (what Prisma
// returns for @db.Date columns) and noon UTC (what createDateOnlyUTC writes).
const UTC_DATE_ONLY_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(?:00|12):00:00(?:\.000)?(?:Z|\+00:00)$/

function getDateOnlyParts(value: string): [number, number, number] | null {
  const match = DATE_ONLY_RE.exec(value) ?? UTC_DATE_ONLY_RE.exec(value)
  if (!match) return null

  const [, year, month, day] = match
  return [Number(year), Number(month), Number(day)]
}

function isUtcDateOnlyInstant(value: Date): boolean {
  if (isNaN(value.getTime())) return false
  const hours = value.getUTCHours()
  return (
    (hours === 0 || hours === 12) &&
    value.getUTCMinutes() === 0 &&
    value.getUTCSeconds() === 0 &&
    value.getUTCMilliseconds() === 0
  )
}

/**
 * Returns true for serialized calendar dates that should not be shifted into
 * the viewer's timezone before display: bare YYYY-MM-DD strings, UTC-midnight
 * instants (@db.Date round-trips) and noon-UTC instants (createDateOnlyUTC).
 */
export function isDateOnlyLikeValue(
  value: Date | string | number | null | undefined,
): boolean {
  if (typeof value === 'string') return getDateOnlyParts(value) !== null
  if (value instanceof Date) return isUtcDateOnlyInstant(value)
  return false
}

/**
 * Format a date string or Date object to a human-readable format
 * @param date Date string or Date object to format
 * @param options Intl.DateTimeFormatOptions to customize the output
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  timezone?: string,
): string {
  if (!date) return ''

  const dateOnly = getDateOnlyForDisplay(date)
  if (dateOnly && isDateOnlyLikeValue(date)) {
    return new Intl.DateTimeFormat('en-US', options).format(dateOnly)
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return ''
  }

  const formatOptions = timezone ? { ...options, timeZone: timezone } : options

  return new Intl.DateTimeFormat('en-US', formatOptions).format(dateObj)
}

/**
 * Format a date using the current user's timezone. Safe to use outside React.
 * Falls back to system timezone if no user is authenticated.
 */
export function formatDateUserTz(
  date: Date | string | null | undefined,
  formatStr: string = DATE_FORMATS.display,
): string {
  if (!date) return ''

  if (isDateOnlyLikeValue(date)) {
    const displayDate = getDateOnlyForDisplay(date)
    return displayDate ? format(displayDate, formatStr) : ''
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  try {
    const timezone = useAuth.getState().getUserTimezone()
    return formatInTimeZone(dateObj, timezone, formatStr)
  } catch {
    return formatInTimeZone(dateObj, 'UTC', formatStr)
  }
}

/**
 * Format a time using the current user's timezone. Safe to use outside React.
 */
export function formatTimeUserTz(
  date: Date | string | null | undefined,
  use24h: boolean = false,
): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  try {
    const timezone = useAuth.getState().getUserTimezone()
    const fmt = use24h ? 'HH:mm' : 'h:mm a'
    return formatInTimeZone(dateObj, timezone, fmt)
  } catch {
    return formatInTimeZone(dateObj, 'UTC', use24h ? 'HH:mm' : 'h:mm a')
  }
}

/**
 * Format a date to relative time (e.g., "2 days ago", "in 3 hours")
 * @param date Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return ''
  }

  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`
  }

  const diffInYears = Math.floor(diffInMonths / 12)
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`
}

/**
 * Format duration in milliseconds to human-readable format
 * @param durationMs Duration in milliseconds
 * @returns Formatted duration string (e.g., "2h 30m", "45m", "1h")
 */
export function formatDuration(durationMs: number): string {
  if (durationMs <= 0) return '0m'

  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  if (hours === 0) {
    return `${minutes}m`
  }

  if (minutes === 0) {
    return `${hours}h`
  }

  return `${hours}h ${minutes}m`
}

/**
 * Creates a date-only value stored as noon UTC to avoid timezone shifts.
 * When a user selects "2026-01-20", we want to store it so it displays
 * as "2026-01-20" regardless of the viewer's timezone.
 *
 * @param dateValue Date string in YYYY-MM-DD format
 * @returns Date object set to noon UTC
 */
export function createDateOnlyUTC(dateValue: string): Date {
  return calendarDateToStorage(dateValue)
}

/**
 * Serialises a picker/typed Date (local midnight in the writer's zone) as the
 * noon-UTC ISO string of the same calendar day. This is what every date-only
 * field should send to the API: `.toISOString()` on the raw Date emits
 * midnight local, which @db.Date columns truncate to the previous day for
 * writers east of UTC and which plain DateTime columns render a day early for
 * viewers west of the writer.
 */
export function toDateOnlyIso(date: Date): string {
  return createDateOnlyUTC(toDateOnlyString(date)).toISOString()
}

/**
 * Converts a date value to a date input string (YYYY-MM-DD).
 * ALWAYS uses UTC-based extraction to avoid timezone shifts.
 * Date-only fields should always be interpreted in UTC to remain consistent
 * regardless of the user's local timezone.
 *
 * @param value Date value to convert
 * @returns Date string in YYYY-MM-DD format
 */
export function toDateInputValue(value?: Date | string | null): string {
  return calendarDateFromStorage(value)
}

/**
 * Converts a Date or ISO string into a date-only string without timezone shifts.
 * - Date objects (from UI pickers) use local date parts.
 * - String values (from storage/API) use UTC date parts.
 */
export function toDateOnlyString(value?: Date | string | null): string {
  return value instanceof Date
    ? calendarDateFromPicker(value)
    : calendarDateFromStorage(value)
}

/** Explicit calendar-only input: bridge stored day parts into a local picker.
 * Never infer a field's meaning from whether its timestamp happens to be midnight. */
export function parseCalendarDateValue(
  value?: Date | string | null,
): Date | null {
  return calendarDateToPicker(calendarDateFromStorage(value))
}

export function parseDateValue(
  value?: Date | string | number | null,
): Date | null {
  if (!value) return null
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'string' && isDateOnlyLikeValue(value)) {
    return getDateOnlyForDisplay(value)
  }

  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Gets a Date object suitable for local formatting from a date-only value.
 * ALWAYS uses UTC date parts to create a local date that represents
 * the same calendar date, avoiding timezone shifts.
 *
 * @param value Date value to convert
 * @returns Date object in local time representing the correct calendar date
 */
export function getDateOnlyForDisplay(
  value?: Date | string | null,
): Date | null {
  return calendarDateToPicker(calendarDateFromStorage(value))
}

/**
 * Formats date-only fields by preserving their stored UTC calendar day.
 * Use this for database @db.Date values that may arrive as either strings or
 * Date objects.
 */
export function formatDateOnly(
  value?: Date | string | null,
  formatStr: string = DATE_FORMATS.display,
): string {
  const displayDate = getDateOnlyForDisplay(value)
  return displayDate ? format(displayDate, formatStr) : ''
}

/**
 * Month-year label ("Mar 2027") for date-only month fields (billingMonth and
 * friends), read in UTC so the stored calendar month never shifts.
 */
export function formatMonthLabel(
  value: Date | string | null | undefined,
  fallback = 'Not set',
): string {
  if (!value) return fallback
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return fallback
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
